import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Zap, Clock, DollarSign } from "@/components/icons";
import { fetchUnit } from "@/lib/api";
import type { ActiveSession } from "@/lib/api";
import { formatNum } from "@/lib/unit-helpers";
import { Badge } from "@/components/ui/badge";
import { formatDuration, formatPower } from "@voltie/localization-web";
import { useT } from "@/app/localization";
import { energyFromWh, viewerInstant } from "@/app/displayFormat";

function durationText(seconds: number): string {
  return formatDuration(seconds > 0 ? seconds : 0, "seconds");
}

// Unknown (null / absent) is "—", never $0.00. The live estimate carries no currency,
// so a known amount keeps its legacy dollar rendering (currency milestone C-5).
function formatCost(cost: number | null | undefined): string {
  return cost == null ? "—" : `$${cost.toFixed(2)}`;
}

interface SessionDetailPanelProps {
  session: ActiveSession;
}

export function SessionDetailPanel({ session: s }: SessionDetailPanelProps) {
  const t = useT();
  const unitQ = useQuery({
    queryKey: ["unit", s.unit_id],
    queryFn: () => fetchUnit(s.unit_id),
    enabled: !!s.unit_id,
    refetchInterval: 5_000,
  });

  const unit = unitQ.data;
  const online = unit?.is_online !== false;

  return (
    <div className="space-y-5">
      {/* Session header */}
      <div>
        <div className="flex items-center gap-3">
          <Zap className="h-5 w-5 text-success" />
          <h2 className="text-xl font-bold">{s.unit_name || s.unit_id}</h2>
        </div>
        <div className="font-mono text-sm opacity-60 mt-1">{s.unit_id}</div>
        <div className="mt-2">
          <Badge variant="success">{t("operator.session.charging_badge")}</Badge>
        </div>
      </div>

      {/* Session metrics */}
      <div className="grid grid-cols-2 gap-3">
        <DetailItem label={t("operator.session_panel.energy_delivered")} value={energyFromWh(s.energy_delivered || 0) ?? "—"} />
        <DetailItem label={t("operator.session.duration")} value={durationText(s.duration_seconds)} />
        <DetailItem label={t("operator.session_panel.total_cost")} value={formatCost(s.costs?.total_cost)} />
        <DetailItem label={t("operator.session.subsessions")} value={String(s.subsessions_count ?? 0)} />
        <DetailItem label={t("operator.session.started")} value={viewerInstant(s.start_time) ?? "—"} />
      </div>

      {/* Cost breakdown */}
      {s.costs && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold opacity-70 uppercase">{t("operator.session_panel.cost_breakdown")}</h3>
          <div className="grid grid-cols-2 gap-3">
            <DetailItem label={t("operator.session_panel.activation_fee")} value={formatCost(s.costs.activation_cost)} />
            <DetailItem label={t("operator.session_panel.energy_cost")} value={formatCost(s.costs.energy_cost)} />
            <DetailItem label={t("operator.session_panel.time_cost")} value={formatCost(s.costs.charging_cost)} />
            <DetailItem label={t("operator.session_panel.idle_cost")} value={formatCost(s.costs.idle_cost)} />
            {/* The top-up to the minimum charge: what the total holds beyond the parts above. */}
            {s.costs.minimum_charge_cost != null && s.costs.minimum_charge_cost !== 0 && (
              <DetailItem label={t("operator.session_panel.minimum_charge_topup")} value={formatCost(s.costs.minimum_charge_cost)} />
            )}
          </div>
        </div>
      )}

      {/* Unit telemetry (if loaded) */}
      {unit && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold opacity-70 uppercase">{t("operator.unit.live_telemetry")}</h3>
          <div className="grid grid-cols-2 gap-3">
            <DetailItem label={t("operator.units.column_status")} value={online ? unit.state_name || t("operator.units.badge_unknown") : t("operator.units.badge_offline")} />
            <DetailItem label={t("operator.metric.voltage")} value={`${formatNum(unit.voltage, 1)}V`} />
            <DetailItem label={t("operator.metric.current")} value={`${formatNum(unit.current, 1)}A`} />
            <DetailItem label={t("operator.metric.power")} value={formatPower(unit.power_kw ?? 0, "kW", "precise")} />
            <DetailItem label={t("operator.metric.power_factor")} value={formatNum(unit.power_factor, 2)} />
          </div>
        </div>
      )}

      {/* Link to full detail */}
      <div className="pt-2 border-t border-border">
        <Link
          to={`/unit/${encodeURIComponent(s.unit_id)}`}
          className="text-sm font-medium text-primary hover:underline"
        >
          {t("operator.session_panel.view_unit")}
        </Link>
      </div>
    </div>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-card rounded-lg p-3">
      <div className="text-xs opacity-60">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}
